import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import LoginPage from "./app/login/page";
import DashboardLayout from "./app/(dashboard)/layout";
import DashboardPage from "./app/(dashboard)/dashboard/page";
import CitizensPage from "./app/(dashboard)/citizens/page";
import NewCitizenPage from "./app/(dashboard)/citizens/new/page";
import EditCitizenPage from "./app/(dashboard)/citizens/[id]/page";
import AuthCallback from "./app/auth/callback/route";
import ReportsPage from "./app/(dashboard)/reports/page";
import { memo, useMemo } from "react";
import CitizenRequestsPage from "./app/(dashboard)/requests/page";
import ContactsPage from "./app/(dashboard)/contacts/page";
import TagsPage from "./app/(dashboard)/tags/page";
import FeaturedPage from "./app/(dashboard)/featured/page";
import SettingsPage from "./app/(dashboard)/settings/page";
import UploadPage from "./app/(dashboard)/upload/page";
import ExternalImagesPage from "./app/(dashboard)/external-images/page";
// Protected route wrapper
const ProtectedRoute = memo(function ProtectedRoute({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
});

// Public route wrapper
const PublicRoute = memo(function PublicRoute({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (user && window.location.pathname !== "/auth/callback") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
});

// Root loader component
const LoadingSpinner = memo(function LoadingSpinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
    </div>
  );
});

const App = memo(function App() {
  const { user, isLoading } = useAuth();

  const rootElement = useMemo(() => {
    if (isLoading) {
      return <LoadingSpinner />;
    }
    return user ? (
      <Navigate to="/dashboard" replace />
    ) : (
      <Navigate to="/login" replace />
    );
  }, [isLoading, user]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={rootElement} />

      {/* Protected routes */}
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/citizens"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CitizensPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/citizens/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <NewCitizenPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/citizens/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EditCitizenPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ReportsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/requests"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CitizenRequestsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ContactsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tags"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TagsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/featured"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <FeaturedPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <UploadPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/external-images"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ExternalImagesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch-all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
});

export default App;
